import { useState, useCallback, useRef, useEffect } from "react";
import { getCustomers } from "@api";
import { buildApiQuery } from "@utils";

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

export default function useProfileCustomers(profileId) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasMoreData, setHasMoreData] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [searchResultCount, setSearchResultCount] = useState(null);

  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const initializedRef = useRef(false);
  const lastSearchRef = useRef("");

  const fetchPage = useCallback(
    async (page, search, append = false) => {
      if (!profileId) return;

      const query = buildApiQuery({
        page,
        pageSize: PAGE_SIZE,
        searchTerm: search || undefined,
        sort: "name",
        sortDirection: "asc",
        filters: [
          {
            key: "paymentProfileId",
            type: "enum",
            value: [profileId],
          },
        ],
      });

      if (page === 1) setLoading(true);

      try {
        const response = await getCustomers(query);
        const data = response?.data || [];
        const total = response?.rows || 0;

        pageRef.current = page;

        if (append) {
          setAccounts((prev) => [...prev, ...data]);
        } else {
          setAccounts(data);
        }

        if (!search) {
          setTotalRows(total);
        }
        setSearchResultCount(search ? total : null);
        setHasMoreData(page * PAGE_SIZE < total);
      } catch (error) {
        console.error("Failed to fetch profile customers:", error);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [profileId],
  );

  // Reset and fetch when profile changes
  useEffect(() => {
    setAccounts([]);
    setSearchTerm("");
    setHasMoreData(false);
    setTotalRows(0);
    setSearchResultCount(null);
    pageRef.current = 1;
    lastSearchRef.current = "";

    if (profileId) {
      initializedRef.current = true;
      fetchPage(1, "");
    } else {
      initializedRef.current = false;
    }
  }, [profileId, fetchPage]);

  const fetchMore = useCallback(() => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    fetchPage(pageRef.current + 1, lastSearchRef.current, true);
  }, [fetchPage]);

  // Debounced server-side search
  useEffect(() => {
    if (!initializedRef.current) return;
    if (searchTerm === lastSearchRef.current) return;

    const timer = setTimeout(() => {
      lastSearchRef.current = searchTerm;
      const trimmed = searchTerm.trim();
      if (!trimmed) {
        setSearchResultCount(null);
      }
      fetchPage(1, trimmed);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm, fetchPage]);

  return {
    accounts,
    loading,
    searchTerm,
    setSearchTerm,
    hasMoreData,
    totalRows,
    searchResultCount,
    fetchMore,
  };
}
